import { z } from 'zod'
import { apiRequest, apiRequestMultipart } from '@/lib/api-client'

export const voiceSourceTypeSchema = z.enum([
  'linkedin_pdf',
  'own_text',
  'blog_post',
  'newsletter',
  'book_excerpt',
  'transcript',
  'website_domain',
])
export type VoiceSourceType = z.infer<typeof voiceSourceTypeSchema>

export const voiceSourceStatusSchema = z.enum([
  'uploaded',
  'analyzing',
  'analyzed',
  'error',
])
export type VoiceSourceStatus = z.infer<typeof voiceSourceStatusSchema>

export const voiceAnalysisFragmentSchema = z
  .object({
    tone: z.string().optional(),
    do_nots: z.array(z.string()).optional(),
    structural_patterns: z.string().optional(),
    hook_examples: z.array(z.string()).optional(),
    channel_rules: z
      .object({
        linkedin: z.string().optional(),
        x: z.string().optional(),
        blog: z.string().optional(),
        email: z.string().optional(),
      })
      .partial()
      .optional(),
    usps: z.array(z.string()).optional(),
    language_variant: z.enum(['de-CH', 'de-DE', 'en-GB', 'en-US']).optional(),
    confidence: z.number().optional(),
    last_updated: z.string().optional(),
    source_count: z.number().optional(),
    argumentation_patterns: z.string().optional(),
    bedrock_count: z.number().optional(),
    evidence_summary: z.string().optional(),
    consolidator_notes: z.string().optional(),
    last_edited_by_user_at: z.string().optional(),
  })
  .partial()
export type VoiceAnalysisFragment = z.infer<typeof voiceAnalysisFragmentSchema>

// User-editierbare Felder. Metadaten (confidence, last_updated, source_count)
// bleiben backend-managed und werden vom Endpoint ignoriert.
export interface VoiceAnalysisUpdate {
  tone?: string
  do_nots?: string[]
  structural_patterns?: string
  hook_examples?: string[]
  channel_rules?: {
    linkedin?: string
    x?: string
    blog?: string
    email?: string
  }
  usps?: string[]
  language_variant?: 'de-CH' | 'de-DE' | 'en-GB' | 'en-US'
}

export const voiceSourceSchema = z.object({
  id: z.string(),
  workspace_id: z.string(),
  brand_profile_id: z.string(),
  source_type: voiceSourceTypeSchema,
  source_name: z.string(),
  storage_path: z.string().nullable(),
  file_mime: z.string().nullable(),
  raw_text: z.string().nullable(),
  extracted_patterns: voiceAnalysisFragmentSchema.default({}),
  status: voiceSourceStatusSchema,
  error_message: z.string().nullable(),
  uploaded_at: z.string(),
  analyzed_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})
export type VoiceSource = z.infer<typeof voiceSourceSchema>

const listResponseSchema = z.object({ items: z.array(voiceSourceSchema) })
const consolidateResponseSchema = z.object({ voice_analysis: voiceAnalysisFragmentSchema })

export async function listVoiceSources(brandId: string): Promise<VoiceSource[]> {
  const res = await apiRequest({
    path: `/brand/profiles/${brandId}/voice-sources`,
    schema: listResponseSchema,
  })
  return res.items
}

export async function uploadVoiceSource(
  brandId: string,
  file: File,
  source_type: VoiceSourceType,
  source_name?: string,
  options?: { skipAnalyze?: boolean },
): Promise<VoiceSource> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('source_type', source_type)
  if (source_name) formData.append('source_name', source_name)
  // Phase 1.8 Bulk-Flow: skip_analyze=true persistiert die Source ohne sofortige
  // LLM-Analyse — der Consolidate-Call analysiert dann alle pending Sources
  // parallel und konsolidiert in einem Rutsch.
  const path = options?.skipAnalyze
    ? `/brand/profiles/${brandId}/voice-sources?skip_analyze=true`
    : `/brand/profiles/${brandId}/voice-sources`
  return await apiRequestMultipart({
    path,
    formData,
    schema: voiceSourceSchema,
  })
}

export async function addWebsiteDomainVoiceSource(
  brandId: string,
  input: { url: string; depth?: number; skipAnalyze?: boolean },
): Promise<VoiceSource> {
  return await apiRequest({
    method: 'POST',
    path: `/brand/profiles/${brandId}/voice-sources/domain`,
    body: input,
    schema: voiceSourceSchema,
  })
}

export async function reanalyzeVoiceSource(brandId: string, sourceId: string): Promise<VoiceSource> {
  return await apiRequest({
    method: 'POST',
    path: `/brand/profiles/${brandId}/voice-sources/${sourceId}/analyze`,
    schema: voiceSourceSchema,
  })
}

export async function consolidateVoice(brandId: string): Promise<VoiceAnalysisFragment> {
  const res = await apiRequest({
    method: 'POST',
    path: `/brand/profiles/${brandId}/consolidate-voice`,
    schema: consolidateResponseSchema,
  })
  return res.voice_analysis
}

export async function deleteVoiceSource(brandId: string, sourceId: string): Promise<void> {
  await apiRequest({
    method: 'DELETE',
    path: `/brand/profiles/${brandId}/voice-sources/${sourceId}`,
    schema: z.object({ success: z.boolean() }),
  })
}

export async function updateVoiceAnalysis(
  brandId: string,
  update: VoiceAnalysisUpdate,
): Promise<VoiceAnalysisFragment> {
  const res = await apiRequest({
    method: 'PATCH',
    path: `/brand/profiles/${brandId}/voice-analysis`,
    schema: z.object({ voice_analysis: voiceAnalysisFragmentSchema }),
    body: update,
  })
  return res.voice_analysis
}
